/*
 * Documentazione fiscale nel carrello (snippets/cart-fiscal-document.liquid).
 *
 * Perche': il checkout (piano Advanced) non si puo' modificare, quindi scelta
 * del documento e dati fiscali si raccolgono nel carrello e viaggiano come
 * attributi del carrello fino all'ordine. Servono solo come informazione:
 * nessuna integrazione SDI.
 *
 * Campi obbligatori: con "Scontrino per detrazione fiscale" serve il codice
 * fiscale, con "Fattura" servono ragione sociale, partita IVA e indirizzo
 * completo (SDI e PEC restano facoltativi). Se ne manca uno, "Procedi
 * all'acquisto" non parte: i campi vuoti si evidenziano, il primo prende il
 * focus e compare l'avviso sotto ciascuno.
 *
 * Solo i campi VUOTI bloccano. Un valore compilato ma non valido (codice
 * fiscale o partita IVA sbagliati, CAP non di 5 cifre...) mostra l'avviso,
 * si salva lo stesso (serve allo staff per ricontattare il cliente) e
 * "Verifica dati fiscali" vale "Da verificare". Quando i campi sono completi,
 * al clic si aspetta solo la fine di un salvataggio in corso.
 *
 * Stato: uno solo per la pagina, in memoria. Il tema ridisegna il carrello
 * laterale e la pagina carrello con innerHTML a ogni aggiornamento: il nuovo
 * elemento riprende lo stato da qui invece che dall'HTML del server, che
 * potrebbe non avere ancora l'ultima digitazione. Pagina e drawer non
 * convivono mai (il drawer non esiste su /cart): fra i due lo stato passa
 * dagli attributi del carrello.
 *
 * Stato iniziale, in quest'ordine: attributi del carrello, metafield del
 * cliente se ha fatto l'accesso, "Solo scontrino". Un "Solo scontrino" senza
 * dati salvato in automatico non conta come scelta, altrimenti chi accede
 * dopo aver aperto il carrello non ritroverebbe i suoi dati: vale solo se il
 * cliente l'ha scelto in questa sessione (sessionStorage).
 */
(() => {
  'use strict';

  if (customElements.get('fiscal-document')) return;

  // Chiavi e valori ESATTI: li leggono le automazioni sull'ordine.
  const OPTIONS = {
    receipt: 'Solo scontrino',
    deduction: 'Scontrino per detrazione fiscale',
    invoice: 'Fattura'
  };
  const KEYS = {
    doc: 'Documento fiscale',
    cf: 'Codice fiscale',
    company: 'Ragione sociale',
    vat: 'Partita IVA',
    address: 'Indirizzo di fatturazione',
    zip: 'CAP di fatturazione',
    city: 'Città di fatturazione',
    province: 'Provincia di fatturazione',
    sdi: 'Codice destinatario SDI',
    pec: 'PEC',
    check: 'Verifica dati fiscali'
  };
  const FIELDS = ['cf', 'company', 'vat', 'address', 'zip', 'city', 'province', 'sdi', 'pec'];
  const ADDRESS_PARTS = ['address', 'zip', 'city', 'province'];
  // Obbligatori per opzione, nell'ordine in cui compaiono nel blocco.
  const REQUIRED = {
    deduction: ['cf'],
    invoice: ['company', 'vat', 'address', 'zip', 'city', 'province']
  };
  const CHOSEN_KEY = 'fiscal-document-chosen';
  const SAVE_DELAY = 400;

  /* ---------- Normalizzazione ---------- */

  const collapse = (value) => value.trim().replace(/\s+/g, ' ');
  const normalize = (field, raw) => {
    const value = raw == null ? '' : String(raw);
    switch (field) {
      case 'cf':
      case 'province':
      case 'sdi':
        return value.replace(/\s+/g, '').toUpperCase();
      case 'company':
      case 'address':
      case 'city':
        return collapse(value);
      case 'vat':
        return value.replace(/\s+/g, '').replace(/^IT/i, '');
      case 'zip':
        return value.replace(/\s+/g, '');
      case 'pec':
        return value.replace(/\s+/g, '').toLowerCase();
      default:
        return value.trim();
    }
  };

  /* ---------- Validazione ---------- */

  const CF_PATTERN = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;
  // Valori dei caratteri in posizione dispari (1a, 3a...) per il controllo.
  const CF_ODD = {
    0: 1, 1: 0, 2: 5, 3: 7, 4: 9, 5: 13, 6: 15, 7: 17, 8: 19, 9: 21,
    A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21, K: 2, L: 4, M: 18,
    N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14, U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23
  };
  const cfEven = (char) => (/[0-9]/.test(char) ? Number(char) : char.charCodeAt(0) - 65);

  const isValidCf = (cf) => {
    if (!CF_PATTERN.test(cf)) return false;
    let sum = 0;
    for (let i = 0; i < 15; i += 1) sum += i % 2 === 0 ? CF_ODD[cf[i]] : cfEven(cf[i]);
    return String.fromCharCode(65 + (sum % 26)) === cf[15];
  };

  const isValidVat = (vat) => {
    if (!/^[0-9]{11}$/.test(vat)) return false;
    let sum = 0;
    for (let i = 0; i < 10; i += 1) {
      let digit = Number(vat[i]);
      if (i % 2 === 1) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
    }
    return (10 - (sum % 10)) % 10 === Number(vat[10]);
  };

  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Per ogni campo dell'opzione scelta: true se c'e' qualcosa da segnalare.
  const evaluate = (state) => {
    const f = state.fields;
    const result = { cf: false, company: false, vat: false, address: false, parts: {}, sdi: false, pec: false, ok: true };

    if (state.option === 'deduction') {
      result.cf = !isValidCf(f.cf);
      result.ok = !result.cf;
    }

    if (state.option === 'invoice') {
      result.company = f.company === '';
      result.vat = !isValidVat(f.vat);
      result.parts = {
        address: f.address === '',
        zip: !/^[0-9]{5}$/.test(f.zip),
        city: f.city === '',
        province: !/^[A-Z]{2}$/.test(f.province)
      };
      result.address = ADDRESS_PARTS.some((part) => result.parts[part]);
      result.sdi = f.sdi !== '' && !/^[A-Z0-9]{6,7}$/.test(f.sdi);
      result.pec = f.pec !== '' && !EMAIL.test(f.pec);
      result.ok = !(result.company || result.vat || result.address || result.sdi || result.pec);
    }

    return result;
  };

  // I campi obbligatori dell'opzione scelta ancora vuoti: bloccano il checkout.
  const missingFields = (state) => (REQUIRED[state.option] || []).filter((field) => state.fields[field] === '');

  // Tutti gli attributi, sempre: quelli delle opzioni non scelte vuoti, cosi'
  // cambiando opzione sull'ordine non restano dati vecchi.
  const attributesFor = (state) => {
    const attributes = {};
    Object.values(KEYS).forEach((key) => (attributes[key] = ''));
    attributes[KEYS.doc] = OPTIONS[state.option];

    const f = state.fields;
    const check = evaluate(state).ok ? 'OK' : 'Da verificare';

    if (state.option === 'deduction') {
      attributes[KEYS.cf] = f.cf;
      attributes[KEYS.check] = check;
    }
    if (state.option === 'invoice') {
      ['company', 'vat', 'address', 'zip', 'city', 'province', 'sdi', 'pec'].forEach((field) => {
        attributes[KEYS[field]] = f[field];
      });
      attributes[KEYS.check] = check;
    }
    return attributes;
  };

  /* ---------- Stato iniziale ---------- */

  const optionFromLabel = (label) => {
    const value = String(label || '').toLowerCase();
    if (value.includes('fattura')) return 'invoice';
    if (value.includes('detrazione')) return 'deduction';
    if (value.includes('scontrino')) return 'receipt';
    return '';
  };

  const readFields = (source) => {
    const fields = {};
    FIELDS.forEach((field) => (fields[field] = normalize(field, source && source[field])));
    return fields;
  };
  const hasData = (fields) => FIELDS.some((field) => fields[field] !== '');

  const chosenThisSession = () => {
    try {
      return window.sessionStorage.getItem(CHOSEN_KEY) === '1';
    } catch (error) {
      return false;
    }
  };
  const markChosen = () => {
    try {
      window.sessionStorage.setItem(CHOSEN_KEY, '1');
    } catch (error) {
      /* niente: senza storage vale la regola generale */
    }
  };

  const initialState = (data) => {
    const cart = data.cart || {};
    const cartFields = readFields(cart);
    const cartOption = optionFromLabel(cart.doc);
    const cartIsChoice = cartOption && (cartOption !== 'receipt' || hasData(cartFields) || chosenThisSession());

    if (cartIsChoice) return { option: cartOption, fields: cartFields };

    const customer = data.customer;
    if (customer) {
      const customerFields = readFields(customer);
      let customerOption = optionFromLabel(customer.doc);
      if (!customerOption && (customerFields.company || customerFields.vat)) customerOption = 'invoice';
      if (!customerOption && customerFields.cf) customerOption = 'deduction';
      if (customerOption) return { option: customerOption, fields: customerFields };
    }

    return { option: cartOption || 'receipt', fields: cartFields };
  };

  /* ---------- Salvataggio ---------- */

  const shared = {
    state: null,
    touched: new Set(),
    // Dopo un clic bloccato su "Procedi all'acquisto": i campi obbligatori
    // vuoti restano segnalati anche se il cliente non li ha ancora toccati.
    showRequired: false,
    instances: new Set(),
    timer: null,
    inFlight: 0,
    queue: Promise.resolve(),
    saved: null
  };

  const updateUrl = () =>
    (window.FoxTheme && window.FoxTheme.routes && window.FoxTheme.routes.cart_update_url) || '/cart/update.js';
  const fetchConfig = () =>
    window.FoxTheme && window.FoxTheme.utils && window.FoxTheme.utils.fetchConfig
      ? window.FoxTheme.utils.fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

  const sameAttributes = (a, b) => !!a && !!b && Object.keys(a).every((key) => (a[key] || '') === (b[key] || ''));

  const save = () => {
    clearTimeout(shared.timer);
    shared.timer = null;

    const attributes = attributesFor(shared.state);
    if (sameAttributes(attributes, shared.saved)) return shared.queue;

    shared.inFlight += 1;
    shared.queue = shared.queue
      .then(() => fetch(updateUrl(), { ...fetchConfig(), body: JSON.stringify({ attributes }) }))
      .then((response) => {
        if (response && response.ok) shared.saved = attributes;
      })
      .catch((error) => console.error('Documentazione fiscale: salvataggio non riuscito', error))
      .finally(() => {
        shared.inFlight -= 1;
      });
    return shared.queue;
  };

  const scheduleSave = () => {
    clearTimeout(shared.timer);
    shared.timer = setTimeout(save, SAVE_DELAY);
  };

  const busy = () => shared.timer !== null || shared.inFlight > 0;
  const flush = () => (shared.timer !== null ? save() : shared.queue);

  /* ---------- Checkout: bloccato se mancano dati, poi aspetta il salvataggio ---------- */

  const ATTENTION = 'fiscal-document__input--attention';
  const reducedMotion = () => !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Fa lampeggiare i campi obbligatori vuoti e porta il cliente sul primo.
  // "missing" e' gia' nell'ordine in cui i campi compaiono a video.
  const highlightMissing = (missing) => {
    let first = null;
    shared.instances.forEach((instance) => {
      missing.forEach((field) => {
        const input = instance.querySelector(`[data-input="${field}"]`);
        if (!input) return;
        // Tolta e rimessa, cosi' l'animazione riparte anche a un secondo clic.
        input.classList.remove(ATTENTION);
        void input.offsetWidth;
        input.classList.add(ATTENTION);
        if (!first && input.getClientRects().length > 0) first = input;
      });
    });
    if (!first) return;
    first.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
    first.focus({ preventScroll: true });
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button[name="checkout"], input[name="checkout"]');
      if (!button || button.dataset.fiscalReady === 'true') return;

      // Senza il blocco fiscale a video (carrello vuoto) non c'e' nulla da chiedere.
      if (shared.state && shared.instances.size > 0) {
        const missing = missingFields(shared.state);
        if (missing.length > 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          shared.showRequired = true;
          shared.instances.forEach((instance) => instance.render({ values: false }));
          highlightMissing(missing);
          return;
        }
      }

      if (!busy()) return;

      event.preventDefault();
      button.setAttribute('aria-busy', 'true');
      flush().then(() => {
        button.removeAttribute('aria-busy');
        button.dataset.fiscalReady = 'true';
        const form = button.form;
        if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit(button);
        } else {
          button.click();
        }
        delete button.dataset.fiscalReady;
      });
    },
    true
  );

  /* ---------- Elemento ---------- */

  class FiscalDocument extends HTMLElement {
    connectedCallback() {
      let data = {};
      try {
        data = JSON.parse(this.querySelector('[data-fiscal-data]').textContent);
      } catch (error) {
        data = {};
      }

      const serverAttributes = {};
      const cart = data.cart || {};
      Object.entries(KEYS).forEach(([field, key]) => (serverAttributes[key] = cart[field] || ''));
      if (shared.saved === null) shared.saved = serverAttributes;

      if (!shared.state) {
        shared.state = initialState(data);
        // I valori gia' presenti contano come toccati: se non sono validi
        // l'avviso si vede subito, senza aspettare che il cliente li modifichi.
        FIELDS.forEach((field) => {
          if (shared.state.fields[field] !== '') shared.touched.add(field);
        });
      }

      shared.instances.add(this);
      this.bind();
      this.render({ values: true });

      if (!sameAttributes(attributesFor(shared.state), serverAttributes)) save();
    }

    disconnectedCallback() {
      shared.instances.delete(this);
    }

    bind() {
      // Un elemento spostato nel DOM si ricollega: gli ascoltatori una volta sola.
      if (this.bound) return;
      this.bound = true;

      this.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          shared.state.option = radio.value;
          // I campi dell'altra opzione non sono ancora stati chiesti: niente rosso.
          shared.showRequired = false;
          markChosen();
          this.broadcast({ values: false });
          save();
        });
      });

      this.querySelectorAll('[data-input]').forEach((input) => {
        const field = input.dataset.input;
        ['animationend', 'animationcancel'].forEach((name) => {
          input.addEventListener(name, () => input.classList.remove(ATTENTION));
        });
        input.addEventListener('input', () => {
          shared.state.fields[field] = normalize(field, input.value);
          this.broadcast({ values: false });
          scheduleSave();
        });
        input.addEventListener('change', () => {
          const value = normalize(field, input.value);
          shared.state.fields[field] = value;
          if (input.value !== value) input.value = value;
          shared.touched.add(field);
          this.broadcast({ values: false });
          scheduleSave();
        });
      });
    }

    // Aggiorna questo elemento e gli altri eventualmente collegati; nei campi
    // di questo non si riscrive il valore mentre il cliente sta digitando.
    broadcast({ values }) {
      shared.instances.forEach((instance) => instance.render({ values: instance === this ? values : true }));
    }

    render({ values }) {
      const state = shared.state;

      this.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.checked = radio.value === state.option;
      });
      this.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.panel !== state.option;
      });

      if (values) {
        this.querySelectorAll('[data-input]').forEach((input) => {
          const value = state.fields[input.dataset.input];
          if (input.value !== value) input.value = value;
        });
      }

      this.renderWarnings();

      const attributes = attributesFor(state);
      this.querySelectorAll('[data-attribute]').forEach((input) => {
        input.value = attributes[input.dataset.attribute] || '';
      });
    }

    renderWarnings() {
      const state = shared.state;
      const result = evaluate(state);
      const shown = (field) => shared.touched.has(field) || state.fields[field] !== '';
      // Dopo un clic bloccato i campi obbligatori vuoti si segnalano subito.
      const missing = shared.showRequired ? missingFields(state) : [];
      const forced = (field) => missing.includes(field);

      const flags = {
        cf: result.cf && (shown('cf') || forced('cf')),
        company: result.company && (shown('company') || forced('company')),
        vat: result.vat && (shown('vat') || forced('vat')),
        sdi: result.sdi,
        pec: result.pec,
        address: result.address && (ADDRESS_PARTS.some(shown) || ADDRESS_PARTS.some(forced))
      };

      // Codice fiscale e partita IVA hanno due avvisi: vuoto (obbligatorio) o
      // compilato ma non valido.
      const reason = (field) => (state.fields[field] === '' ? 'required' : 'invalid');

      ['cf', 'company', 'vat', 'sdi', 'pec'].forEach((field) => {
        this.toggleWarning(field, flags[field], [this.querySelector(`[data-input="${field}"]`)], () => true, reason(field));
      });

      // Un solo avviso per l'indirizzo, sotto il gruppo, con evidenziati
      // i campi da correggere.
      const parts = ADDRESS_PARTS.map((part) => this.querySelector(`[data-input="${part}"]`));
      this.toggleWarning('address', flags.address, parts, (input) => result.parts[input.dataset.input]);
    }

    toggleWarning(name, visible, inputs, isInvalid = () => true, reason = null) {
      const warning = this.querySelector(`[data-warning="${name}"]`);
      if (!warning) return;
      warning.hidden = !visible;
      // Dove l'avviso ha piu' versioni se ne vede una sola.
      warning.querySelectorAll('[data-reason]').forEach((message) => {
        message.hidden = message.dataset.reason !== reason;
      });

      inputs.forEach((input) => {
        if (!input) return;
        if (visible) {
          input.setAttribute('aria-describedby', warning.id);
        } else {
          input.removeAttribute('aria-describedby');
        }
        const invalid = visible && isInvalid(input);
        input.classList.toggle('fiscal-document__input--invalid', invalid);
        if (invalid) {
          input.setAttribute('aria-invalid', 'true');
        } else {
          input.removeAttribute('aria-invalid');
        }
      });
    }
  }

  customElements.define('fiscal-document', FiscalDocument);
})();
